import * as React from "react";
import { useCallback } from "react";
import { ResponsiveModal } from "@wtc/ui";
import { Button } from "./button";
import { FormFeedbackDisplay } from "./states/feedback-display";
import { useFormFeedback } from "@/lib/hooks/forms/use-feedback";
import { Trash2, RotateCcw } from "lucide-react";

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  formManager?: ReturnType<typeof useFormFeedback> & {
    draft?: any;
    clearDraft?: () => void;
    hasDraft?: boolean;
  };
  showClearDraft?: boolean;
  onClearDraft?: () => void;
  className?: string;
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "7xl";
  /** Side for mobile drawer */
  side?: "top" | "bottom" | "left" | "right";
  /** Whether to force modal behavior even on mobile */
  forceModal?: boolean;
  /** Whether to force drawer behavior even on desktop */
  forceDrawer?: boolean;
}

export const FormModal: React.FC<FormModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  formManager,
  showClearDraft = true,
  onClearDraft,
  className = "",
  maxWidth = "2xl",
  side = "bottom",
  forceModal = false,
  forceDrawer = false,
}) => {
  const hasDraft = formManager?.draft && Object.keys(formManager.draft).length > 0;

  const handleClearDraft = useCallback(() => {
    if (formManager?.clearDraft) {
      formManager.clearDraft();
    }
    if (onClearDraft) {
      onClearDraft();
    }
  }, [formManager, onClearDraft]);

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title={title}
      description={description}
      maxWidth={maxWidth}
      side={side}
      forceModal={forceModal}
      forceDrawer={forceDrawer}
      className={className}
      showCloseButton={false} // We'll handle close button ourselves for consistency
    >
      <div className="space-y-4">
        {/* Clear draft button - positioned differently for mobile vs desktop */}
        {showClearDraft && hasDraft && (
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClearDraft}
              className="flex items-center gap-2 text-destructive hover:text-destructive"
              title="Clear unsaved changes"
            >
              <Trash2 size={16} />
              Clear Draft
            </Button>
          </div>
        )}

        {formManager && <FormFeedbackDisplay feedback={formManager} />}

        <div>{children}</div>
      </div>
    </ResponsiveModal>
  );
};
